/* ==============================================================================
   Logo
   ==============================================================================
   A marca do Concord, inline. Não é <img src="mark.svg"> por três motivos
   concretos:

   1. Tema. Os arquivos .svg soltos carregam o valor literal do acento, porque
      custom property de documento não atravessa o limite de um <img>. Inline,
      o gradiente consome var(--color-accent) e a marca acompanha tema claro,
      tema escuro e acento customizado da instância sem nenhum arquivo novo.
   2. Sem request. A marca aparece no login, no cabeçalho e no about; virar
      bytes do bundle sai mais barato que três requests em cold start.
   3. currentColor de verdade. O wordmark herda a cor do texto ao redor, o que
      é o que faz a variante mono funcionar dentro de um parágrafo.

   O que este componente é: geometria. A mesma dos arquivos em
   ./mark.svg e ./logo-horizontal.svg — se um mudar, o outro muda junto.
   ============================================================================== */

import { useId } from 'react';

export type LogoVariant = 'color' | 'mono';

export interface LogoProps {
  /** Altura renderizada em px. A largura acompanha, preservando proporção. */
  size?: number;
  /**
   * 'color' → gradiente de acento (uso padrão).
   * 'mono'  → currentColor, para texto corrido, watermark e impressão.
   */
  variant?: LogoVariant;
  /** Acrescenta o wordmark "Concord" à direita do símbolo. */
  withWordmark?: boolean;
  className?: string;
  /**
   * Rótulo acessível. String vazia marca a marca como decorativa
   * (aria-hidden) — correto quando o nome "Concord" já aparece como texto
   * ao lado, caso em que o leitor de tela anunciaria duas vezes.
   */
  title?: string;
}

/* ------------------------------------------------------------------------------
   Geometria
   ------------------------------------------------------------------------------
   Grade de 48. Quatro nós nos pontos cardeais, a 17 unidades do centro, e as
   arestas do grafo completo K4 entre eles.

   As duas diagonais (norte–sul e leste–oeste) são desenhadas INTERROMPIDAS:
   cada nó emite um radial que para a 8 unidades do centro. É convenção de
   desenho de grafo — a interrupção diz "aqui não há vértice". É também o ponto
   inteiro da marca: o miolo vazado é a ausência de servidor de mídia. Um núcleo
   preenchido diria a arquitetura errada.

   O comprimento do radial foi calibrado renderizando: mais longo que isto os
   quatro braços quase se tocam e a marca vira glifo de "mover"; mais curto,
   viram pontinhos acidentais na borda interna do anel.
------------------------------------------------------------------------------ */
const MARK_BOX = 48;
const LOGO_BOX_W = 217;

/** Perímetro: 4 quadráticas com barriga para fora. Reta demais vira losango
 *  de ícone de sistema; arco de círculo perfeito vira anel genérico. */
const RING =
  'M24 7Q35.89 12.11 41 24Q35.89 35.89 24 41Q12.11 35.89 7 24Q12.11 12.11 24 7Z';

/** Os radiais das duas diagonais, interrompidos antes do centro. */
const SPOKES = 'M24 12v4M36 24h-4M24 36v-4M12 24h4';

/** Traço um pouco mais fino que o do anel: o perímetro é a silhueta, os
 *  radiais são estrutura interna. Iguais, o miolo fica pesado demais. */
const RING_WEIGHT = 3.6;
const SPOKE_WEIGHT = 3.2;
const NODE_RADIUS = 5;

const NODES: ReadonlyArray<readonly [number, number]> = [
  [24, 7],
  [41, 24],
  [24, 41],
  [7, 24],
];

/**
 * Wordmark em geometria monolinha, com a MESMA espessura de traço do símbolo
 * (3.6 na grade de 48). É o que costura marca e nome em um só objeto em vez de
 * parecerem dois elementos colados.
 *
 * Sem texto e sem @font-face: 'C' e 'c' são arcos de 260°, 'o' e a barriga do
 * 'd' são dois semicírculos, 'n' e 'r' são haste + arco. Nada aqui depende de
 * fonte instalada, o que também vale para o SVG exportado.
 */
const WORDMARK: readonly string[] = [
  'M87.27 15.07A11.85 11.85 0 1 0 87.27 33.23',
  'M103.05 19.8A8.1 8.1 0 1 1 103.05 36A8.1 8.1 0 1 1 103.05 19.8',
  'M117.75 35.7V27.6A7.5 7.5 0 0 1 132.75 27.6V35.7',
  'M152.43 21.51A8.1 8.1 0 1 0 152.43 34.29',
  'M167.4 19.8A8.1 8.1 0 1 1 167.4 36A8.1 8.1 0 1 1 167.4 19.8',
  'M182.1 35.7V27.6A7.5 7.5 0 0 1 189.6 20.1',
  'M205.2 19.8A8.1 8.1 0 1 1 205.2 36A8.1 8.1 0 1 1 205.2 19.8M213.3 11.7V35.7',
];

/**
 * Abaixo disto os radiais deixam de ser radiais e passam a ser sujeira: o
 * radial tem 4 das 48 unidades da grade, ou seja menos de 2px em 24px — nesse
 * tamanho ele se funde ao nó e vira borrão. Some com eles e sobra o anel de
 * pares com o centro vazado, que é a parte da ideia que ainda lê pequeno.
 */
const SPOKES_MIN_SIZE = 24;

/** Segundo matiz do gradiente. Não é token porque só existe aqui e na tela de
 *  login: é o acento (275) puxado para 320, mesma luminosidade e mesmo croma. */
const ACCENT_FAR = 'oklch(60% 0.19 320)';

export function Logo({
  size = 32,
  variant = 'color',
  withWordmark = false,
  className = '',
  title = 'Concord',
}: LogoProps) {
  /* Dois logos na mesma página com o mesmo id de gradiente: o segundo referencia
     o primeiro e, se o primeiro desmontar, o segundo fica sem pintura. useId
     resolve na raiz — id colidido em <defs> é bug silencioso e difícil de achar. */
  const uid = useId();
  const gradientId = `concord-accent-${uid}`;
  const titleId = `concord-title-${uid}`;

  const decorative = title === '';
  const showSpokes = size >= SPOKES_MIN_SIZE;

  const viewBoxWidth = withWordmark ? LOGO_BOX_W : MARK_BOX;
  const paint = variant === 'mono' ? 'currentColor' : `url(#${gradientId})`;

  return (
    <svg
      /* A altura manda; a largura é derivada. Assim trocar withWordmark não
         muda o alinhamento vertical de quem já posicionou o componente. */
      width={(size * viewBoxWidth) / MARK_BOX}
      height={size}
      viewBox={`0 0 ${viewBoxWidth} ${MARK_BOX}`}
      className={className}
      role="img"
      aria-labelledby={decorative ? undefined : titleId}
      aria-hidden={decorative ? true : undefined}
    >
      {!decorative && <title id={titleId}>{title}</title>}

      {variant === 'color' && (
        <defs>
          {/* userSpaceOnUse, não o default objectBoundingBox: no default cada
              elemento pintado recebe a rampa inteira dentro da própria caixa, e
              os quatro nós sairiam todos iguais em vez de participarem de uma
              única passagem de cor pela marca.

              stop-color vai em `style` e não em atributo de propósito: var() só
              é interpretado em declaração CSS, nunca em atributo de apresentação
              — em atributo o valor seria descartado e a cor cairia para preto. */}
          <linearGradient
            id={gradientId}
            gradientUnits="userSpaceOnUse"
            x1="0"
            y1="0"
            x2={MARK_BOX}
            y2={MARK_BOX}
          >
            <stop offset="0" style={{ stopColor: 'var(--color-accent)' }} />
            <stop offset="1" style={{ stopColor: ACCENT_FAR }} />
          </linearGradient>
        </defs>
      )}

      <g
        fill="none"
        stroke={paint}
        strokeWidth={RING_WEIGHT}
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d={RING} />
        {showSpokes && <path d={SPOKES} strokeWidth={SPOKE_WEIGHT} />}
      </g>

      <g fill={paint}>
        {NODES.map(([cx, cy]) => (
          <circle key={`${cx}-${cy}`} cx={cx} cy={cy} r={NODE_RADIUS} />
        ))}
      </g>

      {withWordmark && (
        /* O nome sempre em currentColor, inclusive na variante colorida: o
           símbolo carrega o acento, o nome carrega a cor do texto. Wordmark em
           gradiente competiria com o símbolo e ficaria ilegível sobre o próprio
           acento (botão, badge, header colorido). */
        <g
          fill="none"
          stroke="currentColor"
          strokeWidth={RING_WEIGHT}
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          {WORDMARK.map((d) => (
            <path key={d} d={d} />
          ))}
        </g>
      )}
    </svg>
  );
}
